/**
 * Офлайн-персист кэша запросов.
 *
 * Отдельный модуль, а не часть providers.tsx: выход из системы (lib/auth)
 * обязан стереть кэш, а providers обязан знать про сессию. Держать обе связи
 * в одном файле — это цикл импорта, который пришлось бы разрывать
 * динамическим import(). Лучше вынести общий кусок вниз.
 *
 * КЛЮЧ ВКЛЮЧАЕТ ID ПОЛЬЗОВАТЕЛЯ, И ЭТО ГЛАВНОЕ.
 * Aida-PWA запрещает класть ответы /v1/* в Cache Storage: тот ключуется по
 * URL и не разделяется по Authorization, поэтому один пользователь может
 * получить ответ другого. Здесь офлайн нужен, поэтому запрет соблюдается
 * иначе: данные лежат в IndexedDB под ключом с id пользователя и стираются
 * при выходе. Не переносить это в Cache Storage.
 */
import { del, get, keys, set } from 'idb-keyval'
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client'

const CACHE_PREFIX = 'aida.scout.qc.'

/** Персистер поверх IndexedDB для конкретного пользователя. */
export function createPersister(userId: string): Persister {
  const key = `${CACHE_PREFIX}${userId}`
  return {
    persistClient: async (client: PersistedClient) => {
      await set(key, client)
    },
    restoreClient: async () => get<PersistedClient>(key),
    removeClient: async () => {
      await del(key)
    },
  }
}

/** Ничего не сохраняет. Используется, пока пользователь не установлен. */
export const noopPersister: Persister = {
  persistClient: async () => undefined,
  restoreClient: async () => undefined,
  removeClient: async () => undefined,
}

/** Стирает персист ВСЕХ пользователей. Вызывается при выходе. */
export async function clearPersistedCache(): Promise<void> {
  const all = await keys()
  await Promise.all(
    all
      .filter((k): k is string => typeof k === 'string' && k.startsWith(CACHE_PREFIX))
      .map((k) => del(k)),
  )
}
